import {
  Box,
  Button,
  TextField,
  IconButton,
  makeStyles,
  Paper,
  Typography,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import CopyIcon from '@material-ui/icons/FileCopy';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import HomeLayout from '~/src/HomeLayout/components/HomeLayout';
import useTitle from '~/src/hooks/useTitle';
import {
  pendingApiKeyState,
  apiKeyListState,
  newApiKeyState,
} from '../store';
import useResetDevelop from '../hooks/useResetDevelop';
import useApiKeys from '../hooks/useApiKeys';
import ConfirmationDialog from '~/src/components/ConfirmationDialog';

const useStyles = makeStyles(({ spacing }) => ({
  paper: {
    padding: 20,
    marginBottom: spacing(8),
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  credsLabel: {
    fontWeight: 700,
    marginRight: spacing(2),
    minWidth: 125,
  },
  modalKeyField: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    marginTop: spacing(3),
  },
  modalKeyValue: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    backgroundColor: '#f5f5f5',
    padding: spacing(1, 2),
    borderRadius: 4,
    wordBreak: 'break-all',
    flex: 1,
  },
  modalExpires: {
    marginTop: spacing(2),
  },
  tableActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing(2),
  },
  noData: {
    textAlign: 'center',
    padding: spacing(4, 0),
  },
  formGroup: {
    marginBottom: spacing(3),
  },
}));

const apiKeyNameFormName = 'apiKeyName';

const SCOPE_LABELS: Record<string, string> = {
  'tools:read': 'Read Only',
  'tools:execution': 'Execute Models',
  'tools:write': 'All',
};

const API_KEY_NAME_MAX_LENGTH = 50;

const EXPIRATION_OPTIONS = [
  { value: '30days', label: '30 days', days: 30 },
  { value: '60days', label: '60 days', days: 60 },
  { value: 'never', label: 'Never', days: null },
];

// eslint-disable-next-line react/prop-types
const CopyClipboardButton: React.FC<{ tooltip: string, text: string, [key: string]: any }> = ({ tooltip, text, ...rest }) => (
  <Tooltip title={tooltip}>
    <IconButton
      size="small"
      edge="end"
      aria-label="copy"
      onClick={() => window.navigator.clipboard.writeText(text)}
      {...rest}
    >
      <CopyIcon />
    </IconButton>
  </Tooltip>);

const Develop = () => {
  const { t } = useTranslation('develop');
  const classes = useStyles();
  const apiKeys = useApiKeys();
  const [newKey, setNewKey] = useRecoilState(newApiKeyState);
  const apiKeyList = useRecoilValue(apiKeyListState);
  const pendingApiKeys = useRecoilValue(pendingApiKeyState);
  const resetDevelop = useResetDevelop();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokingKeyId, setRevokingKeyId] = useState(null);
  const [apiKeyName, setApiKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['tools:read']);
  const [selectedExpiration, setSelectedExpiration] = useState<string>('never');

  const handleScopeToggle = (scope: string) => {
    setSelectedScopes((prev) => {
      const isChecking = !prev.includes(scope);

      if (scope === 'tools:write') {
        if (isChecking) {
          return [...new Set([...prev, 'tools:write', 'tools:execution'])];
        }
        return prev.filter((s) => s !== 'tools:write');
      }

      if (scope === 'tools:execution' && !isChecking) {
        return prev.filter((s) => s !== 'tools:execution' && s !== 'tools:write');
      }

      return prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope];
    });
  };

  const handleRevokeApiKey = (id: string) => {
    setRevokingKeyId(id);
    setRevokeDialogOpen(true);
  };

  const handleConfirmRevokeApiKey = () => {
    if (revokingKeyId) {
      apiKeys.revoke(revokingKeyId);
      setNewKey(null);
    }
    setRevokeDialogOpen(false);
    setRevokingKeyId(null);
  };

  const handleCancelRevokeApiKey = () => {
    setRevokeDialogOpen(false);
    setRevokingKeyId(null);
  };

  const resetApikeyForm = () => {
    setApiKeyName('');
    setSelectedScopes(['tools:read']);
    setSelectedExpiration('never');
  };

  const handleApiKeyCreate = () => {
    const expirationOption = EXPIRATION_OPTIONS.find((o) => o.value === selectedExpiration);
    const expiresAt = expirationOption?.days
      ? new Date(Date.now() + expirationOption.days * 86400000).toISOString()
      : null;

    apiKeys.create({ name: apiKeyName.trim(), scopes: selectedScopes, expiresAt });
  };

  const handleOpenCreateModal = () => {
    resetApikeyForm();
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    resetApikeyForm();
    setCreateModalOpen(false);
  };

  const handleCloseKeyDialog = async () => {
    setNewKey(null);
    resetApikeyForm();
    setCreateModalOpen(false);
    await apiKeys.read();
  };

  useTitle('Develop');

  useEffect(() => {
    apiKeys.read();
  }, []);

  useEffect(() => () => {
    resetDevelop();
    resetApikeyForm();
  }, []);

  const formatExpiry = (expiresAt: string | null): string => {
    if (!expiresAt) return 'Never';
    return new Date(expiresAt).toLocaleDateString();
  };

  const formatScopes = (scopes: string[]): string => scopes.map((s) => SCOPE_LABELS[s] || s).join(', ');

  return (
    <HomeLayout>
      <Box py={8} px={5} width="100%" minHeight="100px" position="relative">
        <Paper elevation={2} className={classes.paper}>
        <Box className={classes.tableActions}>
          <Typography variant="h5">{t('apiKey.title')}</Typography>
          <Button
            variant="contained"
            onClick={handleOpenCreateModal}
          >
            {t('apiKey.buttons.create')}
          </Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('apiKey.tableHeaders.name')}</TableCell>
                <TableCell>{t('apiKey.tableHeaders.scope')}</TableCell>
                <TableCell>{t('apiKey.tableHeaders.expiry')}</TableCell>
                <TableCell align="right">{t('apiKey.tableHeaders.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeyList && apiKeyList.length > 0 ? (
                apiKeyList.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell>
                      <Typography noWrap variant="body2" title={item.name}>{item.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatScopes(item.scopes)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatExpiry(item.expiresAt)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('apiKey.buttons.revoke')}>
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label="revoke"
                          onClick={() => handleRevokeApiKey(item._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="textSecondary" className={classes.noData}>
                      {t('apiKey.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create API Key Modal */}
      <Dialog open={createModalOpen} onClose={handleCloseCreateModal}>
        <DialogTitle disableTypography>
          <Typography variant="h5">{t('apiKey.buttons.create')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box className={classes.formGroup}>
            <TextField
              fullWidth
              autoComplete="off"
              name={apiKeyNameFormName}
              variant="outlined"
              placeholder={t('apiKey.namePlaceholder')}
              onChange={(e) => setApiKeyName(e.target.value)}
              value={apiKeyName}
              inputProps={{ maxLength: API_KEY_NAME_MAX_LENGTH }}
              helperText={`${apiKeyName.length}/${API_KEY_NAME_MAX_LENGTH}`}
            />
          </Box>
          <Box className={classes.formGroup}>
            <Typography variant="subtitle2" gutterBottom>
              {t('apiKey.scopesLabel')}
            </Typography>
            <FormGroup>
              {['tools:read', 'tools:execution', 'tools:write'].map((scope) => (
                <FormControlLabel
                  key={scope}
                  control={
                    <Checkbox
                      checked={selectedScopes.includes(scope)}
                      onChange={() => handleScopeToggle(scope)}
                      disabled={scope === 'tools:read'}
                    />
                  }
                  label={SCOPE_LABELS[scope]}
                />
              ))}
            </FormGroup>
          </Box>
          <Box className={classes.formGroup}>
            <Typography variant="subtitle2" gutterBottom>
              {t('apiKey.expirationLabel')}
            </Typography>
            <RadioGroup value={selectedExpiration} onChange={(e) => setSelectedExpiration(e.target.value)}>
              {EXPIRATION_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateModal}>Cancel</Button>
          <Button
            disabled={!apiKeyName.trim() || selectedScopes.length === 0 || pendingApiKeys}
            variant="contained"
            onClick={handleApiKeyCreate}
          >
            {t('apiKey.buttons.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Created Success Dialog */}
      {newKey && (
        <Dialog open onClose={handleCloseKeyDialog}>
          <DialogTitle disableTypography>
            <Typography variant="h5">{t('apiKey.title')}</Typography>
          </DialogTitle>
          <DialogContent>
            <Typography color="error" gutterBottom>
              {t('apiKey.warning')}
            </Typography>
            <Box className={classes.modalKeyField}>
              <Typography className={classes.credsLabel}>{t('apiKey.key')}: </Typography>
              <Typography className={classes.modalKeyValue}>{newKey.key}</Typography>
              <CopyClipboardButton
                tooltip={t('apiKey.buttons.copy')}
                text={newKey.key}
              />
            </Box>
            {newKey.expiresAt && (
              <Typography variant="caption" color="textSecondary" className={classes.modalExpires}>
                {t('apiKey.expiresAt')}: {new Date(newKey.expiresAt).toLocaleDateString()}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={handleCloseKeyDialog}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Revoke Confirmation Dialog */}
      <ConfirmationDialog
        open={revokeDialogOpen}
        onClose={handleCancelRevokeApiKey}
        onConfirm={handleConfirmRevokeApiKey}
        title={t('apiKey.revokeConfirm.title')}
        content={t('apiKey.revokeConfirm.content')}
        confirmButtonLabel={t('apiKey.revokeConfirm.confirm')}
        closeButtonLabel={t('apiKey.revokeConfirm.cancel')}
        primaryButtonColor="secondary"
      />
      </Box>
    </HomeLayout>
  );
};

export default React.memo(Develop);
